import { z } from "zod";
import { PaginationQuerySchema } from "./common.js";
import { ProjectSchema } from "./projects.js";
export const CrmPipelineStageKindValues = ["open", "won", "lost"];
export const CrmDealStatusValues = ["open", "won", "lost", "archived"];
export const CrmDealPriorityValues = ["urgent", "high", "medium", "low"];
export const CrmPipelineStageKindSchema = z.enum(CrmPipelineStageKindValues);
export const CrmDealStatusSchema = z.enum(CrmDealStatusValues);
export const CrmDealPrioritySchema = z.enum(CrmDealPriorityValues);
const CrmDateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const CrmPipelineStageSchema = z.object({
    id: z.string().uuid(),
    pipelineId: z.string().uuid(),
    key: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
    sortOrder: z.number().int().nonnegative(),
    probability: z.number().int().min(0).max(100),
    kind: CrmPipelineStageKindSchema,
    requiredFields: z.array(z.string()).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CrmPipelineSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    description: z.string().nullable(),
    isDefault: z.boolean(),
    archivedAt: z.string().datetime().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    stages: z.array(CrmPipelineStageSchema).default([]),
});
export const CrmDealRevenueSignalSchema = z.object({
    revenueScore: z.number().int().min(0).max(100),
    priority: CrmDealPrioritySchema,
    nextBestAction: z.string().min(1).max(160),
    riskReasons: z.array(z.string().min(1).max(160)).default([]),
    actionDue: z.boolean(),
    daysUntilClose: z.number().int().nullable(),
    daysSinceUpdated: z.number().int().nonnegative(),
});
export const CrmDealSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    companyId: z.string().uuid().nullable(),
    companyName: z.string().nullable().default(null),
    primaryContactId: z.string().uuid().nullable(),
    primaryContactName: z.string().nullable().default(null),
    pipelineId: z.string().uuid(),
    stageId: z.string().uuid(),
    stageKey: z.string().min(1).max(80),
    stageName: z.string().min(1).max(120),
    stageKind: CrmPipelineStageKindSchema,
    ownerEmail: z.string().email().nullable(),
    amountMinor: z.number().int().nonnegative(),
    currency: z.string().length(3),
    probability: z.number().int().min(0).max(100),
    weightedAmountMinor: z.number().int().nonnegative(),
    expectedCloseDate: z.string().nullable().default(null),
    nextStep: z.string().nullable().default(null),
    nextActionAt: z.string().datetime().nullable().default(null),
    source: z.string().max(80).nullable().default(null),
    status: CrmDealStatusSchema,
    closedAt: z.string().datetime().nullable().default(null),
    closedBy: z.string().email().nullable().default(null),
    lossReason: z.string().max(120).nullable().default(null),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    ...CrmDealRevenueSignalSchema.shape,
});
export const CrmDealCreateSchema = z.object({
    name: z.string().min(1).max(200),
    companyId: z.string().uuid().nullable().optional(),
    primaryContactId: z.string().uuid().nullable().optional(),
    pipelineId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
    ownerEmail: z.string().email().nullable().optional(),
    amountMinor: z.number().int().nonnegative().default(0),
    currency: z.string().length(3).default("GBP"),
    probability: z.number().int().min(0).max(100).optional(),
    expectedCloseDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    nextStep: z.string().max(5000).nullable().optional(),
    nextActionAt: z.string().datetime().nullable().optional(),
    source: z.string().max(80).nullable().optional(),
});
export const CrmDealUpdateSchema = CrmDealCreateSchema.partial().omit({
    pipelineId: true,
});
export const CrmDealStageMoveSchema = z.object({
    stageId: z.string().uuid(),
    probability: z.number().int().min(0).max(100).optional(),
});
export const CrmDealCloseWonSchema = z.object({
    stageId: z.string().uuid().optional(),
});
export const CrmDealCloseLostSchema = z.object({
    stageId: z.string().uuid().optional(),
    lossReason: z.string().min(1).max(120),
});
export const CrmDealProjectHandoffSchema = z.object({
    projectName: z.string().min(1).max(200).optional(),
    shareWithPortal: z.boolean().default(false),
    createStarterTasks: z.boolean().default(true),
});
export const CrmDealProjectHandoffResponseSchema = z.object({
    deal: CrmDealSchema,
    project: ProjectSchema,
    created: z.boolean(),
    starterTaskCount: z.number().int().nonnegative(),
});
export const CrmDealListQuerySchema = PaginationQuerySchema.extend({
    status: CrmDealStatusSchema.optional(),
    ownerEmail: z.string().email().optional(),
    stageId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    staleOnly: z.coerce.boolean().optional(),
});
export const CrmDealListResponseSchema = z.object({
    deals: z.array(CrmDealSchema),
    total: z.number().int(),
});
export const CrmPipelineListResponseSchema = z.object({
    pipelines: z.array(CrmPipelineSchema),
});
export const CrmRevenueDashboardQuerySchema = z.object({
    ownerEmail: z.string().email().optional(),
    source: z.string().min(1).max(80).optional(),
    stageId: z.string().uuid().optional(),
    closeFrom: CrmDateOnlySchema.optional(),
    closeTo: CrmDateOnlySchema.optional(),
});
export const CrmRevenueStageSummarySchema = z.object({
    stageId: z.string().uuid(),
    stageKey: z.string().min(1).max(80),
    stageName: z.string().min(1).max(120),
    probability: z.number().int().min(0).max(100),
    dealCount: z.number().int().nonnegative(),
    grossAmountMinor: z.number().int().nonnegative(),
    weightedAmountMinor: z.number().int().nonnegative(),
});
export const CrmRevenueOwnerSummarySchema = z.object({
    ownerEmail: z.string().email().nullable(),
    dealCount: z.number().int().nonnegative(),
    grossAmountMinor: z.number().int().nonnegative(),
    weightedAmountMinor: z.number().int().nonnegative(),
    overdueActionCount: z.number().int().nonnegative(),
});
export const CrmRevenueDashboardSchema = z.object({
    generatedAt: z.string().datetime(),
    currency: z.string().length(3),
    filters: CrmRevenueDashboardQuerySchema.default({}),
    openDealCount: z.number().int().nonnegative(),
    openGrossAmountMinor: z.number().int().nonnegative(),
    openWeightedAmountMinor: z.number().int().nonnegative(),
    overdueActionCount: z.number().int().nonnegative(),
    staleDealCount: z.number().int().nonnegative(),
    unassignedDealCount: z.number().int().nonnegative(),
    hotLeads: z.array(CrmDealSchema),
    stageSummaries: z.array(CrmRevenueStageSummarySchema),
    ownerSummaries: z.array(CrmRevenueOwnerSummarySchema),
});
export const CrmDealParamSchema = z.object({
    id: z.string().uuid(),
});
//# sourceMappingURL=crm-deals.js.map